import { useAuthStore } from '../store/auth'

const API_URL = import.meta.env.VITE_API_URL || ''

interface RequestOptions {
  method?: string
  body?: unknown
  requiresAuth?: boolean
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, requiresAuth = true } = options
  const { installId, installSecret, apiKey } = useAuthStore.getState()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (requiresAuth) {
    if (installId && installSecret) {
      headers['x-officex-install-id'] = installId
      headers['x-officex-install-secret'] = installSecret
    } else if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`
    }
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

// Auth
export async function officexLogin(installId: string, installSecret: string, customerId?: string) {
  return request<{
    user_id: string
    api_key: string
    email?: string
    username?: string
    credits_balance?: number
    nocodb_jobs_view_url?: string
    nocodb_scheduled_view_url?: string
  }>('/auth/officex-login', {
    method: 'POST',
    body: {
      officex_install_id: installId,
      officex_install_secret: installSecret,
      officex_customer_id: customerId,
    },
    requiresAuth: false,
  })
}

export async function emailLogin(email: string, password: string) {
  return request<{
    user_id: string
    api_key: string
    email?: string
    username?: string
    credits_balance?: number
    nocodb_jobs_view_url?: string
    nocodb_scheduled_view_url?: string
  }>('/auth/login', {
    method: 'POST',
    body: { email, password },
    requiresAuth: false,
  })
}

export async function register(email: string, password: string) {
  return request<{
    user_id: string
    api_key: string
    email?: string
  }>('/auth/register', {
    method: 'POST',
    body: { email, password },
    requiresAuth: false,
  })
}

// User settings
export async function updatePassword(currentPassword: string, newPassword: string) {
  return request<{ success: boolean }>('/api/v1/users/password', {
    method: 'PATCH',
    body: { current_password: currentPassword, new_password: newPassword },
  })
}

export async function rotateApiKey() {
  return request<{ api_key: string }>('/api/v1/users/rotate-api-key', {
    method: 'POST',
  })
}

export async function getCreditsBalance() {
  return request<{ balance: number }>('/api/v1/users/credits')
}

// Jobs
export async function createJob(data: {
  content_prompt: string
  output_quantity?: number
  media_mode: string
  filter_prompt?: string
  publish_mode?: string
  schedule_prompt?: string
  notes?: string
  on_job_finish_webhook?: string
  on_job_finish_email?: string
  on_schedule_webhook_default?: string
  on_schedule_email_default?: string
  tracer?: string
  inbox_tracer?: string
}) {
  return request('/api/v1/jobs', { method: 'POST', body: data })
}

export async function listJobs(params?: {
  limit?: number
  page?: string
  bookmarked?: boolean
  search?: string
}) {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.page) searchParams.set('page', params.page)
  if (params?.bookmarked) searchParams.set('bookmarked', 'true')
  if (params?.search) searchParams.set('search', params.search)
  const qs = searchParams.toString()
  return request<{ jobs: unknown[]; next_page?: string }>(`/api/v1/jobs${qs ? `?${qs}` : ''}`)
}

export async function getJob(jobId: string) {
  return request(`/api/v1/jobs/${jobId}`)
}

export async function updateJob(jobId: string, data: {
  title?: string
  notes?: string
  bookmarked?: boolean
}) {
  return request(`/api/v1/jobs/${jobId}`, {
    method: 'PATCH',
    body: data,
  })
}

export async function cancelJob(jobId: string) {
  return request(`/api/v1/jobs/${jobId}`, {
    method: 'PATCH',
    body: { action: 'cancel' },
  })
}

// Results
export async function listResults(jobId: string, params?: {
  limit?: number
  page?: string
  bookmarked?: boolean
  search?: string
  match_score_min?: number
}) {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.page) searchParams.set('page', params.page)
  if (params?.bookmarked) searchParams.set('bookmarked', 'true')
  if (params?.search) searchParams.set('search', params.search)
  if (params?.match_score_min !== undefined) searchParams.set('match_score_min', String(params.match_score_min))
  const qs = searchParams.toString()
  return request<{ results: unknown[]; next_page?: string }>(
    `/api/v1/${jobId}/results${qs ? `?${qs}` : ''}`
  )
}

export async function updateResult(jobId: string, resultId: string, data: {
  approved?: boolean
  user_notes?: string
  bookmarked?: boolean
  on_schedule_webhook?: string
  on_schedule_email?: string
  instruction_text?: string
  caption?: string
  hashtags?: string[]
}) {
  return request(`/api/v1/${resultId}?job_id=${jobId}`, {
    method: 'PATCH',
    body: data,
  })
}

export async function scheduleResult(jobId: string, resultId: string, data: {
  scheduled_datetime: string
  on_schedule_webhook?: string
  on_schedule_email?: string
  on_proof_webhook?: string
  instruction_text?: string
  password_protected?: string
}) {
  return request(`/api/v1/${resultId}/schedule?job_id=${jobId}`, {
    method: 'POST',
    body: data,
  })
}

// Scheduled
export async function listScheduled(startDate?: string, endDate?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set('start_date', startDate)
  if (endDate) params.set('end_date', endDate)
  return request<{ scheduled: unknown[] }>(`/api/v1/scheduled?${params}`)
}

export async function getCalendarMonth(year: number, month: number, inboxTracers?: string) {
  const params = inboxTracers ? `?inbox_tracers=${inboxTracers}` : ''
  return request(`/api/v1/calendar/month/${year}/${month}${params}`)
}

export async function updateScheduled(scheduledId: string, data: {
  scheduled_datetime?: string
  instruction_text?: string
  on_schedule_webhook?: string
  on_schedule_email?: string
  password_protected?: string
}) {
  return request(`/api/v1/scheduled/${scheduledId}`, {
    method: 'PATCH',
    body: data,
  })
}

export async function deleteScheduled(scheduledId: string) {
  return request(`/api/v1/scheduled/${scheduledId}`, {
    method: 'DELETE',
  })
}

// Volunteer (public, no auth)
export async function getVolunteerView(scheduledId: string) {
  return request(`/api/v1/volunteer/${scheduledId}`, { requiresAuth: false })
}

export async function submitProof(scheduledId: string, proofUrl: string, password?: string) {
  return request<{
    success: boolean
    proof_url: string
    proof_timestamp: string
    next_task?: { scheduled_id: string }
  }>(`/api/v1/volunteer/${scheduledId}/proof`, {
    method: 'POST',
    body: { proof_url: proofUrl, password },
    requiresAuth: false,
  })
}

export async function getNextVolunteerTask(inboxTracer: string) {
  return request<{
    has_next: boolean
    scheduled_id?: string
    scheduled_datetime?: string
    message?: string
  }>(`/api/v1/volunteer/next/${inboxTracer}`, { requiresAuth: false })
}
